import React, {
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  FormEvent,
} from "react";

import { isEmpty, without } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import { BillingDocumentAction } from "actions";

import { RecipientList } from "./RecipientList";
import { ReviewAndSend } from "components/BillingWorkFlow/Send/ReviewAndSend";
import {
  BottomSticky,
  Button,
  FormCollapse,
  SecondaryButton,
} from "components/Form";
import { CenteredGreedy, Column, Row } from "components/Layout";
import MessageBox from "components/MessageBox";
import { AbsolutePaper } from "components/Paper";

import { EMPTY_ARRAY, EMPTY_OBJECT } from "lib";
import { caseInsensitiveCompare } from "lib/compare";

import {
  getDefaultBillingSettings,
  getIsFetchingBillingRunDocumentAttachments,
  selectBillingDocumentsAggridDataById,
  selectEmailRecipientsByActiveBillingDocumentId,
  selectRelatedAttachmentIdsByBillingDocumentIdLookup,
} from "selectors";

import { useToggle } from "hooks";
import { BusinessModalSection } from "constants/navigation";
import { openEditBusinessModal } from "lib/navigation";
import { ClickableLink } from "components/Link";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@fortawesome/pro-solid-svg-icons";
import { SubtleBadge } from "components/Badge";
import { AttachmentComponent } from "./AttachmentList";
import { Reports } from "constants/reports";

import LoadingSpinner from "components/LoadingSpinner";
import { toggleRecipientReducer } from "./toggleRecipientReducer";
import { ReportSendingList } from "../ReportsList";

interface BillingDocumentRow {
  billingDocument: BillingDocument;
}

export type IdType = number | string;

export interface ToggleRecipientAction {
  billingDocumentId?: string;
  businessUser?: BusinessBusinessUser;
  emailRecipient?: EmailRecipient;
  rawEmailAddress?: string;
  remove?: IdType[];
  add?: Partial<ToggleRecipientState>;
}

export interface ToggleRecipientState {
  businessUsers?: BusinessBusinessUser[];
  emailRecipients?: EmailRecipient[];
  rawEmailAddress?: string;
}

interface RecipientState {
  businessUsers: BusinessBusinessUser[];
  emailRecipients: EmailRecipient[];
  rawEmailAddress?: string;
}

type SelectedAttachmentIdsByBillingDocumentId = Record<number, IdType[]>;

interface BillingDocumentSendingOptionsProps {
  billingDocuments: BillingDocumentRow[];
  onClose: () => void;
}

interface SendPayloadItem {
  billing_document_id: IdType;
  email_recipients: Array<{
    id: IdType;
    type: string;
  }>;
  raw_email_address: string;
  email_template_text: string;
  business_user_ids: IdType[];
  attachment_ids: IdType[];
  report_slugs: string[];
}

const sortKey = (billingDocumentRow: BillingDocumentRow): string =>
  `${billingDocumentRow.billingDocument.type} ${
    billingDocumentRow.billingDocument.number ?? ""
  }`;

const toggleSection = (
  openSections: IdType[],
  setOpenSections: React.Dispatch<React.SetStateAction<IdType[]>>,
  newSection: IdType,
): void => {
  if (openSections.includes(newSection)) {
    setOpenSections(openSections.filter(item => item !== newSection));
  } else {
    setOpenSections([...openSections, newSection]);
  }
};

const BillingDocumentSendingOptions = ({
  billingDocuments,
  onClose,
}: BillingDocumentSendingOptionsProps): React.ReactElement => {
  const [
    selectedAttachmentIdsByBillingDocumentId,
    setSelectedAttachmentIdsByBillingDocumentId,
  ] = useState<SelectedAttachmentIdsByBillingDocumentId>({});

  const selectedBillingDocumentIds = billingDocuments.map(
    row => row.billingDocument.id,
  );

  const [openRecipientSections, setOpenRecipientSections] = useState<IdType[]>(
    selectedBillingDocumentIds,
  );

  const [selectedShowAllDocumentIds, setSelectedShowAllDocumentIds] = useState<
    IdType[]
  >([]);

  // When documents are selected - ensure the recipients section for them are open by default
  useEffect(() => {
    if (billingDocuments.length) {
      setOpenRecipientSections(
        billingDocuments.map(row => row.billingDocument.id),
      );
    }
  }, [billingDocuments]);

  const [openAttachmentSections, setOpenAttachmentSections] = useState<
    IdType[]
  >([]);

  const [openReportSections, setOpenReportSections] = useState<IdType[]>([]);

  const [showConfirm, toggleShowConfirm] = useToggle(false);

  const dispatch = useDispatch();

  const aggridDataByBillingDocumentLookup = useSelector(
    selectBillingDocumentsAggridDataById,
  );

  const emailRecipientsByBillingDocumentIdLookup = useSelector(
    selectEmailRecipientsByActiveBillingDocumentId,
  );

  const isFetchingBillingRunDocumentAttachments = useSelector(
    getIsFetchingBillingRunDocumentAttachments,
  );

  const reportsAvailableForSending = Object.values(Reports).filter(
    report => report.allowAttachedToBillingDocumentEmail,
  );

  const billingSettings = useSelector(getDefaultBillingSettings);

  const initialSelectedReports = useMemo(
    () =>
      billingSettings?.attachRfidReportWithBillingDocumentEmails
        ? [Reports.RFIDListCSVView.slug]
        : EMPTY_ARRAY,
    [billingSettings?.attachRfidReportWithBillingDocumentEmails],
  );

  const relatedAttachmentIdsByBillingDocumentIdLookup = useSelector(
    selectRelatedAttachmentIdsByBillingDocumentIdLookup,
  );

  const [toggleRecipientState, toggleRecipientAction] = useReducer(
    toggleRecipientReducer as React.Reducer<
      ToggleRecipientState,
      ToggleRecipientAction
    >,
    billingDocuments.reduce<ToggleRecipientState>((acc, row) => {
      const { id } = row.billingDocument;
      acc[id] = {
        businessUsers: [],
        emailRecipients: (
          emailRecipientsByBillingDocumentIdLookup[id] || EMPTY_ARRAY
        ).filter((er: EmailRecipient) => er.isAccountsRecipient),
      };
      return acc;
    }, {}),
  );

  const knownRecipientIdsByBillingDocumentIdRef = useRef<Record<
    IdType,
    Set<IdType>
  > | null>(null);

  const toggleRecipientStateRef =
    useRef<ToggleRecipientState>(toggleRecipientState);

  function onToggleAttachment(
    billingDocumentId: IdType,
    attachmentId: IdType,
  ): void {
    setSelectedAttachmentIdsByBillingDocumentId(prev => {
      const existing = prev[billingDocumentId] || [];
      const isSelected = existing.includes(attachmentId);

      return {
        ...prev,
        [billingDocumentId]: isSelected
          ? existing.filter(id => id !== attachmentId)
          : [...existing, attachmentId],
      };
    });
  }

  const [
    selectedReportSlugsByBillingDocumentId,
    setSelectedReportSlugsByBillingDocumentId,
  ] = useState<Record<IdType, string[]>>({});

  function onToggleReport(billingDocumentId: IdType, reportSlug: string) {
    setSelectedReportSlugsByBillingDocumentId(prev => {
      const existing = prev[billingDocumentId] || [];
      const isSelected = existing.includes(reportSlug);

      return {
        ...prev,
        [billingDocumentId]: isSelected
          ? existing.filter(slug => slug !== reportSlug)
          : [...existing, reportSlug],
      };
    });
  }

  // When documents are selected set all relevant attachments as selected.
  useEffect(() => {
    if (isFetchingBillingRunDocumentAttachments) {
      return;
    }

    setSelectedAttachmentIdsByBillingDocumentId(prev => {
      const newState: SelectedAttachmentIdsByBillingDocumentId = {};

      billingDocuments.forEach(({ billingDocument }) => {
        const billingDocumentId = billingDocument.id;

        const attachmentIds =
          relatedAttachmentIdsByBillingDocumentIdLookup[billingDocumentId] ||
          EMPTY_ARRAY;

        if (!(billingDocumentId in prev)) {
          newState[billingDocumentId] = attachmentIds;
        } else {
          newState[billingDocumentId] = prev[billingDocumentId];
        }
      });

      return newState;
    });
  }, [
    billingDocuments,
    relatedAttachmentIdsByBillingDocumentIdLookup,
    isFetchingBillingRunDocumentAttachments,
  ]);

  const sortedBillingDocuments = useMemo(() => {
    return [...billingDocuments].sort((a, b) =>
      caseInsensitiveCompare(sortKey(a), sortKey(b)),
    );
  }, [billingDocuments]);

  // Keep a ref of the latest toggleRecipientState.
  // Using a ref avoids adding toggleRecipientState as a useEffect dependency
  // (which would cause unwanted re-renders)
  useEffect(() => {
    toggleRecipientStateRef.current = toggleRecipientState;
  }, [toggleRecipientState]);

  useEffect(() => {
    const currentState = toggleRecipientStateRef.current;

    // Add any new ones (documents with no state yet).
    const additions = billingDocuments.reduce<Partial<ToggleRecipientState>>(
      (acc, { billingDocument }) => {
        if (isEmpty(currentState[billingDocument.id])) {
          acc[billingDocument.id] = {
            businessUsers: [],
            emailRecipients: (
              emailRecipientsByBillingDocumentIdLookup[billingDocument.id] || []
            ).filter((er: EmailRecipient) => er.isAccountsRecipient),
          };
        }
        return acc;
      },
      {},
    );

    const currentIdsByDocId = billingDocuments.reduce<
      Record<IdType, Set<IdType>>
    >((acc, { billingDocument }) => {
      const recipients =
        emailRecipientsByBillingDocumentIdLookup[billingDocument.id] || [];
      acc[billingDocument.id] = new Set(
        recipients.map((er: EmailRecipient) => er.id),
      );
      return acc;
    }, {});

    const knownIdsRef = knownRecipientIdsByBillingDocumentIdRef;
    const isFirstRun = knownIdsRef.current === null;

    if (isFirstRun) {
      knownIdsRef.current = currentIdsByDocId;
    } else {
      billingDocuments.forEach(({ billingDocument }) => {
        const { id } = billingDocument;
        const currentRecipients =
          emailRecipientsByBillingDocumentIdLookup[id] || [];
        const previousIds = knownIdsRef.current[id] || new Set<IdType>();

        const newRecipients = currentRecipients.filter(
          (er: EmailRecipient) => !previousIds.has(er.id),
        );

        const newAccountsRecipients = newRecipients.filter(
          (er: EmailRecipient) => er.isAccountsRecipient,
        );

        if (newAccountsRecipients.length > 0) {
          const existing = currentState[id] || (EMPTY_OBJECT as RecipientState);
          const existingEmailRecipients = existing.emailRecipients || [];

          additions[id] = {
            businessUsers: existing.businessUsers || [],
            emailRecipients: [
              ...existingEmailRecipients,
              ...newAccountsRecipients.filter(
                newRecipient =>
                  !existingEmailRecipients.some(
                    existingRecipient =>
                      existingRecipient.id === newRecipient.id,
                  ),
              ),
            ],
            rawEmailAddress: existing.rawEmailAddress || "",
          };
        }
      });

      // Update ref to current lookup ids (only for current billingDocuments; removed docs drop out).
      knownIdsRef.current = currentIdsByDocId;
    }

    // Remove any non-existent ones.
    const inValidKeys = without(
      Object.keys(currentState),
      ...billingDocuments.map(({ billingDocument }) => billingDocument.id),
    ) as IdType[];

    if (!isEmpty(inValidKeys) || !isEmpty(additions)) {
      toggleRecipientAction({ remove: inValidKeys, add: additions });
    }
  }, [billingDocuments, emailRecipientsByBillingDocumentIdLookup]);

  useEffect(() => {
    setSelectedReportSlugsByBillingDocumentId(previousState => {
      const nextState = { ...previousState };
      let hasChanges = false;

      billingDocuments.forEach(({ billingDocument }) => {
        const billingDocumentId = billingDocument.id;

        // New document: initialize with billing settings reports
        if (!(billingDocumentId in nextState)) {
          nextState[billingDocumentId] = initialSelectedReports;
          hasChanges = true;
        }
        return;
      });

      return hasChanges ? nextState : previousState;
    });
  }, [billingDocuments, initialSelectedReports]);

  const onSend = (emailTemplateText: string): void => {
    const payload: SendPayloadItem[] = Object.entries(toggleRecipientState)
      .map(([billingDocumentId, destinations]) => ({
        billing_document_id: billingDocumentId,
        email_recipients: destinations.emailRecipients.map(er => ({
          id: er.id,
          type: er.source.type,
        })),
        raw_email_address: destinations.rawEmailAddress || "",
        email_template_text: emailTemplateText,
        business_user_ids: destinations.businessUsers.map(bu => bu.id),
        attachment_ids:
          selectedAttachmentIdsByBillingDocumentId[billingDocumentId] || [],
        report_slugs:
          selectedReportSlugsByBillingDocumentId[billingDocumentId] || [],
      }))
      .filter(
        ({ email_recipients, raw_email_address, business_user_ids }) =>
          email_recipients.length > 0 ||
          Boolean(raw_email_address) ||
          business_user_ids.length > 0,
      );

    dispatch(BillingDocumentAction.email(payload));
    onClose();
  };

  function onClickEditBusiness(businessId: IdType): void {
    openEditBusinessModal(
      businessId,
      undefined,
      BusinessModalSection.EMAIL_RECIPIENTS,
    );
  }

  if (billingDocuments.length === 0) {
    return (
      <AbsolutePaper>
        <Column full>
          <MessageBox>Select documents to send</MessageBox>
        </Column>
      </AbsolutePaper>
    );
  }

  return (
    <AbsolutePaper>
      <form
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          toggleShowConfirm();
          e.preventDefault();
        }}
      >
        <h1>Sending Options</h1>

        {sortedBillingDocuments.map(({ billingDocument }) => {
          const { business } =
            aggridDataByBillingDocumentLookup[billingDocument.id];

          const billingDocumentRecipientState = (toggleRecipientState[
            billingDocument.id
          ] || EMPTY_OBJECT) as ToggleRecipientState;

          const selectedEmailRecipientIds =
            billingDocumentRecipientState.emailRecipients?.map(er => er.id) ||
            EMPTY_ARRAY;

          const selectedBusinessUserIds =
            billingDocumentRecipientState.businessUsers?.map(er => er.id) ||
            EMPTY_ARRAY;

          const rawEmailAddress =
            billingDocumentRecipientState.rawEmailAddress || "";

          const emailsSelected =
            selectedBusinessUserIds.length > 0 ||
            selectedEmailRecipientIds.length > 0 ||
            !!rawEmailAddress;

          const relatedBillingDocumentAttachmentIds =
            relatedAttachmentIdsByBillingDocumentIdLookup[billingDocument.id] ||
            EMPTY_ARRAY;

          const selectedBillingDocumentAttachments =
            selectedAttachmentIdsByBillingDocumentId[billingDocument.id] ||
            EMPTY_ARRAY;

          const showAllRecipients = selectedShowAllDocumentIds.includes(
            billingDocument.id,
          );

          const selectedBillingDocumentReportSlugs =
            selectedReportSlugsByBillingDocumentId[billingDocument.id] ||
            EMPTY_ARRAY;

          return (
            <React.Fragment key={billingDocument.id}>
              <Row justifyBetween padding={2}>
                <strong>
                  {billingDocument.type}
                  {billingDocument.number}
                </strong>
                <div>
                  <ClickableLink
                    onClick={() => onClickEditBusiness(business.id)}
                  >
                    <FontAwesomeIcon icon={faPencil} />
                    &nbsp;<strong>{business.name}</strong>{" "}
                  </ClickableLink>
                </div>
              </Row>

              <FormCollapse
                isOpen={openRecipientSections.includes(billingDocument.id)}
                onToggle={() =>
                  toggleSection(
                    openRecipientSections,
                    setOpenRecipientSections,
                    billingDocument.id,
                  )
                }
                header={
                  <SubtleBadge hasErrorBadge={!emailsSelected}>
                    Email Recipients
                  </SubtleBadge>
                }
                hasErrorBadge={!emailsSelected}
              >
                <RecipientList
                  showAll={showAllRecipients}
                  toggleShowAll={() =>
                    toggleSection(
                      selectedShowAllDocumentIds,
                      setSelectedShowAllDocumentIds,
                      billingDocument.id,
                    )
                  }
                  key={billingDocument.id}
                  billingDocumentId={billingDocument.id}
                  toggleRecipientAction={toggleRecipientAction}
                  toggleRecipientState={billingDocumentRecipientState}
                />
              </FormCollapse>

              {isFetchingBillingRunDocumentAttachments ? (
                <CenteredGreedy>
                  <LoadingSpinner
                    size={14}
                    subjectName="Billing Document Attachments"
                  />
                </CenteredGreedy>
              ) : (
                relatedBillingDocumentAttachmentIds.length > 0 && (
                  <FormCollapse
                    isOpen={openAttachmentSections.includes(billingDocument.id)}
                    onToggle={() =>
                      toggleSection(
                        openAttachmentSections,
                        setOpenAttachmentSections,
                        billingDocument.id,
                      )
                    }
                    header={<SubtleBadge>Attachments</SubtleBadge>}
                  >
                    {(relatedBillingDocumentAttachmentIds as IdType[]).map(
                      attachmentId => (
                        <AttachmentComponent
                          key={attachmentId}
                          attachmentId={attachmentId}
                          selectedAttachmentIds={
                            selectedBillingDocumentAttachments
                          }
                          onToggle={() =>
                            onToggleAttachment(billingDocument.id, attachmentId)
                          }
                        />
                      ),
                    )}
                  </FormCollapse>
                )
              )}
              {reportsAvailableForSending.length > 0 && (
                <FormCollapse
                  isOpen={openReportSections.includes(billingDocument.id)}
                  onToggle={() =>
                    toggleSection(
                      openReportSections,
                      setOpenReportSections,
                      billingDocument.id,
                    )
                  }
                  header={
                    <SubtleBadge hasErrorBadge={false}>Reports</SubtleBadge>
                  }
                  hasErrorBadge={false}
                >
                  {reportsAvailableForSending.map(report => (
                    <ReportSendingList
                      key={report.slug}
                      report={report}
                      onToggle={() =>
                        onToggleReport(billingDocument.id, report.slug)
                      }
                      selectedBillingDocumentReportSlugs={
                        selectedBillingDocumentReportSlugs
                      }
                    />
                  ))}
                </FormCollapse>
              )}
            </React.Fragment>
          );
        })}

        <BottomSticky>
          <Row full background="white">
            <SecondaryButton type="button" onClick={onClose}>
              Cancel
            </SecondaryButton>
            <Button
              disabled={isFetchingBillingRunDocumentAttachments}
              type="submit"
            >
              Review Message
            </Button>
          </Row>
        </BottomSticky>
      </form>

      {showConfirm && (
        <ReviewAndSend onSend={onSend} onClose={toggleShowConfirm} />
      )}
    </AbsolutePaper>
  );
};

export default BillingDocumentSendingOptions;
